import { useMemo, useState } from 'react'
import type { DataTablePagination } from '../types'

/**
 * Hook to handle client-side pagination.
 *
 * Two modes:
 * - Server-side (callbacks provided): returns rows as-is, caller pre-slices
 * - Client-side (no callbacks): manages internal page/pageSize state, slices rows automatically
 *
 * Returns activePage/activePageSize/callbacks so DataTableCustomized can pass
 * unified props to PaginationFooter regardless of mode.
 */
export const useDataTableClientPagination = <T>(
    rows: T[],
    pagination?: DataTablePagination
) => {
    const isClientSidePagination =
        pagination && !pagination.onPageChange && !pagination.onPageSizeChange

    // Internal state for client-side mode
    const [internalPage, setInternalPage] = useState(pagination?.page ?? 1)
    const [internalPageSize, setInternalPageSize] = useState(
        pagination?.pageSize ?? 20
    )

    // Active values: internal state in client mode, props in server mode
    const activePage = isClientSidePagination
        ? internalPage
        : (pagination?.page ?? 1)
    const activePageSize = isClientSidePagination
        ? internalPageSize
        : (pagination?.pageSize ?? 20)

    // Slice rows only in client-side mode
    const paginatedRows = useMemo(() => {
        if (!isClientSidePagination || !pagination) {
            return rows
        }
        const startIndex = (activePage - 1) * activePageSize
        return rows.slice(startIndex, startIndex + activePageSize)
    }, [isClientSidePagination, rows, activePage, activePageSize])

    const totalPages = pagination
        ? Math.ceil(pagination.total / activePageSize)
        : 0

    const pageSizeOptions = pagination?.pageSizeOptions || [20, 50, 100]

    // Expose callbacks for client-side mode so PaginationFooter runs in controlled mode
    const onPageChange = isClientSidePagination
        ? (page: number) => setInternalPage(page)
        : pagination?.onPageChange
    const onPageSizeChange = isClientSidePagination
        ? (size: number) => {
              setInternalPageSize(size)
              setInternalPage(1)
          }
        : pagination?.onPageSizeChange

    return {
        isClientSidePagination,
        paginatedRows,
        totalPages,
        pageSizeOptions,
        activePage,
        activePageSize,
        onPageChange,
        onPageSizeChange,
    }
}
